import usePermissions from './usePermissions';
import useAuthStore from '../store/authStore';

/**
 * useLearningPermissions
 * ----------------------
 * Learning-center permission gates consumed by LearningDashboardPage and
 * related surfaces. Wraps `usePermissions` and adds:
 *   - learning-specific creation gates (student / lesson / group / survey)
 *   - `teacherOwnsGroup(group)` for client-side scope filtering
 *   - `teacherScope` — the teacher's uid when role=teacher, else null
 *
 * Back-end still enforces authorization; this hook exists so UI can hide
 * affordances the user can't act on, and so teachers can be scoped to
 * their own groups in lists/filters without the UI shape changing.
 */
export default function useLearningPermissions() {
  const perms = usePermissions();
  const user = useAuthStore((s) => s.user);
  const role = user?.role;

  const isManager = perms.isManager;
  const isTeacher = perms.isTeacher;
  const isStudent = role === 'student';

  const teacherScope = isTeacher ? user?.uid || user?.id || null : null;

  return {
    ...perms,

    // Canonical learning flags
    canViewAllStudents: isManager,
    canCreateStudent:   isManager,            // teacher creates via atomic new-student-lesson only
    canCreateLesson:    isManager || isTeacher,
    canCreateGroup:     isManager,
    canRecordPayment:   isManager,
    canSendWhatsApp:    isManager || isTeacher,
    canViewAnalytics:   isManager,

    // Teacher scope
    teacherScope,
    teacherOwnsGroup: (group) => {
      if (!group) return false;
      if (isManager) return true;
      if (!isTeacher || !teacherScope) return false;
      if (group.teacherId === teacherScope) return true;
      if (Array.isArray(group.teacherIds) && group.teacherIds.includes(teacherScope)) return true;
      return false;
    },

    // Deny-all for students (safe default)
    isStudent,
  };
}
